/**
 * Exporter/importer swap hook.
 *
 * Uses plain string values for tradeDirection ("exports" | "imports") and
 * view ("markets" | "products") so the controls package does not depend on
 * @atlas-int/data enums.  The string values match the canonical enum values,
 * so callers can pass `TradeDirection.Exports` or the literal "exports" — both work.
 */

interface SwapState {
  importer: string;
  exporter: string;
  tradeDirection: string;
  view: string;
  /** The world group ID to compare against */
  worldGroupId: string;
}

interface SwapResult {
  importer: string;
  exporter: string;
  tradeDirection: string;
}

/**
 * Pure function that computes the result of swapping exporter and importer.
 * Returns the new values without any side effects.
 */
export const computeExporterImporterSwap = (state: SwapState): SwapResult => {
  const { importer, exporter, tradeDirection, view, worldGroupId } = state;

  // After swap: newExporter = old importer, newImporter = old exporter
  const newExporter = importer;
  const newImporter = exporter;
  const isNewWorldExporter = newExporter === worldGroupId;
  const isNewWorldImporter = newImporter === worldGroupId;

  let newTradeDirection: string;

  if (view === "products") {
    // Products view: determine tradeDirection based on NEW positions
    if (isNewWorldExporter && isNewWorldImporter) {
      // Both World: toggle direction (ambiguous case)
      newTradeDirection =
        tradeDirection === "exports" ? "imports" : "exports";
    } else if (isNewWorldExporter) {
      // World -> Country: imports
      newTradeDirection = "imports";
    } else {
      // Country -> World or Country -> Country: exports
      newTradeDirection = "exports";
    }
  } else {
    // Markets view: toggle direction when World is involved
    if (isNewWorldExporter || isNewWorldImporter) {
      newTradeDirection =
        tradeDirection === "exports" ? "imports" : "exports";
    } else {
      // Both non-world (shouldn't happen in Markets view, but default to exports)
      newTradeDirection = "exports";
    }
  }

  return {
    importer: newImporter,
    exporter: newExporter,
    tradeDirection: newTradeDirection,
  };
};

interface UseHandleExporterImporterSwapOptions {
  importer: string;
  exporter: string;
  tradeDirection: string;
  view: string;
  worldGroupId: string;
  onSwap: (result: SwapResult) => void;
  /** Optional analytics callback */
  onAnalytics?: () => void;
}

/**
 * Hook that returns a handler for swapping exporter and importer.
 * All state is provided via options; the result is communicated via onSwap callback.
 */
export const useHandleExporterImporterSwap = (options: UseHandleExporterImporterSwapOptions) => {
  const { importer, exporter, tradeDirection, view, worldGroupId, onSwap, onAnalytics } = options;

  return () => {
    const result = computeExporterImporterSwap({
      importer,
      exporter,
      tradeDirection,
      view,
      worldGroupId,
    });
    onSwap(result);
    onAnalytics?.();
  };
};
